/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package analytics

import kotlinx.datetime.LocalDateTime
import models.TimeSeriesPoint

/**
 * Enhanced analytics data for trend chart visualization
 * Contains both historical data and forecasting projections
 */
data class AdvancedAnalyticsData(
    val originalData: List<Pair<String, Double>>, // Historical monthly spending data
    val trendDirection: TrendDirection,           // Trend analysis results
    val anomalies: List<Anomaly>,                // Detected spending anomalies
    val forecast: List<Double>,                   // Forecasted future spending values
    val recommendations: List<String>,            // Generated spending recommendations
    val categoryData: Map<String, List<Pair<String, Double>>> = emptyMap(), // Per-category monthly data
    val availableCategories: List<String> = emptyList(), // List of all available categories
    val selectedCategory: String? = null          // Currently selected category (null = all)
)

/**
 * Cross-platform function to apply advanced analytics to monthly spending data
 * This provides consistent analytics processing across Web, iOS, and Android platforms
 */
fun applyAdvancedAnalyticsToSpendingData(
    monthlySpending: List<Pair<String, Double>>, 
    categoryData: Map<String, List<Pair<String, Double>>> = emptyMap(),
    availableCategories: List<String> = emptyList(),
    selectedCategory: String? = null
): AdvancedAnalyticsData {
    println("⚡ applyAdvancedAnalyticsToSpendingData: Starting with ${monthlySpending.size} data points")
    println("⚡ applyAdvancedAnalyticsToSpendingData: Raw input data: ${monthlySpending.take(3)}")
    // Debug: Show ALL input data points
    println("⚡ applyAdvancedAnalyticsToSpendingData: ALL INPUT DATA:")
    monthlySpending.forEachIndexed { index, pair ->
        if (pair.second > 0.0) {
            println("⚡ applyAdvancedAnalyticsToSpendingData: INPUT[$index] '${pair.first}' -> ${pair.second} (NON-ZERO)")
        } else {
            println("⚡ applyAdvancedAnalyticsToSpendingData: INPUT[$index] '${pair.first}' -> ${pair.second}")
        }
    }
    
    if (monthlySpending.isEmpty()) {
        println("⚡ applyAdvancedAnalyticsToSpendingData: Empty data, returning NO_DATA result")
        return AdvancedAnalyticsData(
            originalData = monthlySpending,
            trendDirection = TrendDirection("NO_DATA", 0.0, 0.0, 0.0),
            anomalies = emptyList(),
            forecast = emptyList(),
            recommendations = listOf("No data available for analysis. Start tracking your spending to see insights.")
        )
    }
    
    println("⚡ applyAdvancedAnalyticsToSpendingData: Extracting values")
    // Extract spending values for analysis
    val values = monthlySpending.map { it.second }
    println("⚡ applyAdvancedAnalyticsToSpendingData: Got values: ${values.take(3)}... (showing first 3)")
    
    println("⚡ applyAdvancedAnalyticsToSpendingData: Creating TimeSeriesPoint objects")
    // Create TimeSeriesPoint objects using the actual period labels
    val timeSeriesPoints = monthlySpending.map { (periodLabel, value) ->
        println("⚡ applyAdvancedAnalyticsToSpendingData: Creating TimeSeriesPoint for period='$periodLabel' -> value=$value")
        // Parse the period label to create proper datetime
        val dateTime = try {
            when {
                // Handle "JUL 2025", "SEP 2024" format
                periodLabel.matches("[A-Z]{3} \\d{4}".toRegex()) -> {
                    val parts = periodLabel.split(" ")
                    val monthName = parts[0]
                    val year = parts[1].toInt()
                    val monthNumber = when (monthName) {
                        "JAN" -> 1; "FEB" -> 2; "MAR" -> 3; "APR" -> 4
                        "MAY" -> 5; "JUN" -> 6; "JUL" -> 7; "AUG" -> 8
                        "SEP" -> 9; "OCT" -> 10; "NOV" -> 11; "DEC" -> 12
                        else -> 1
                    }
                    println("⚡ applyAdvancedAnalyticsToSpendingData: Parsed '$periodLabel' -> year=$year, month=$monthNumber")
                    kotlinx.datetime.LocalDateTime(year, monthNumber, 1, 0, 0)
                }
                // Handle current month (should now be "SEP 2025" format, but keeping as fallback)
                periodLabel == "This Month" -> {
                    val currentMonthLabel = utils.DateUtils.calculatePeriodLabel("Monthly", 0)
                    // Parse the current month label like "SEP 2025"
                    val parts = currentMonthLabel.split(" ")
                    val monthName = parts[0]
                    val year = parts[1].toInt()
                    val monthNumber = when (monthName) {
                        "JAN" -> 1; "FEB" -> 2; "MAR" -> 3; "APR" -> 4
                        "MAY" -> 5; "JUN" -> 6; "JUL" -> 7; "AUG" -> 8
                        "SEP" -> 9; "OCT" -> 10; "NOV" -> 11; "DEC" -> 12
                        else -> 9 // fallback to September
                    }
                    kotlinx.datetime.LocalDateTime(year, monthNumber, 1, 0, 0)
                }
                // Handle weekly periods like "JUL 21 - 27"
                periodLabel.contains(" - ") -> {
                    kotlinx.datetime.LocalDateTime(2025, 7, 21, 0, 0) // Default to July 2025
                }
                else -> {
                    println("⚡ applyAdvancedAnalyticsToSpendingData: Unknown period format: '$periodLabel', using default")
                    kotlinx.datetime.LocalDateTime(2024, 1, 1, 0, 0)
                }
            }
        } catch (e: Exception) {
            println("⚡ applyAdvancedAnalyticsToSpendingData: Error parsing period '$periodLabel': $e")
            kotlinx.datetime.LocalDateTime(2024, 1, 1, 0, 0)
        }
        
        TimeSeriesPoint(
            dateTime = dateTime,
            value = value
        )
    }
    println("⚡ applyAdvancedAnalyticsToSpendingData: Created ${timeSeriesPoints.size} TimeSeriesPoints")
    
    println("⚡ applyAdvancedAnalyticsToSpendingData: Creating TrendDirectionCalculator")
    // Calculate advanced trend direction with linear regression
    val trendCalculator = TrendDirectionCalculator()
    println("⚡ applyAdvancedAnalyticsToSpendingData: Calling calculateTrendDirection")
    val trendDirection = trendCalculator.calculateTrendDirection(timeSeriesPoints)
    println("⚡ applyAdvancedAnalyticsToSpendingData: Got trend direction: ${trendDirection.direction}")
    
    println("⚡ applyAdvancedAnalyticsToSpendingData: Creating AnomalyDetector")
    // Detect spending anomalies using statistical analysis
    val anomalyDetector = AnomalyDetector()
    println("⚡ applyAdvancedAnalyticsToSpendingData: Calling detectAnomalies")
    val anomalies = anomalyDetector.detectAnomalies(timeSeriesPoints)
    println("⚡ applyAdvancedAnalyticsToSpendingData: Detected ${anomalies.size} anomalies")
    
    println("⚡ applyAdvancedAnalyticsToSpendingData: Calling ForecastingService.generateProjections")
    // Generate forecasting projections for next 3 months
    val forecast = ForecastingService.generateProjections(values, 3)
    println("⚡ applyAdvancedAnalyticsToSpendingData: Generated ${forecast.size} forecast values")
    
    println("⚡ applyAdvancedAnalyticsToSpendingData: Creating RecommendationEngine")
    // Generate intelligent recommendations
    val recommendationEngine = RecommendationEngine()
    val patterns = SpendingPatterns() // Basic empty patterns for now
    val avgSpending = values.average()
    println("⚡ applyAdvancedAnalyticsToSpendingData: Calling generateRecommendations with avgSpending=$avgSpending")
    val recommendations = recommendationEngine.generateRecommendations(
        trendDirection, 
        anomalies,
        patterns,
        avgSpending,
        null // No category information available at this level
    ).map { it.message } // Extract just the message strings
    println("⚡ applyAdvancedAnalyticsToSpendingData: Generated ${recommendations.size} recommendations")
    
    println("⚡ applyAdvancedAnalyticsToSpendingData: Creating final AdvancedAnalyticsData")
    return AdvancedAnalyticsData(
        originalData = monthlySpending,
        trendDirection = trendDirection,
        anomalies = anomalies,
        forecast = forecast,
        recommendations = recommendations,
        categoryData = categoryData,
        availableCategories = availableCategories,
        selectedCategory = selectedCategory
    )
}

/**
 * Simple forecasting service for generating spending projections
 */
object ForecastingService {
    fun generateProjections(historicalValues: List<Double>, months: Int): List<Double> {
        println("🔢 ForecastingService: Input values = $historicalValues, months = $months")
        
        if (historicalValues.size < 2) {
            return List(months) { historicalValues.firstOrNull() ?: 0.0 }
        }
        
        // Simple linear trend projection
        val n = historicalValues.size
        val x = (1..n).map { it.toDouble() }
        val y = historicalValues
        
        println("🔢 ForecastingService: n = $n")
        println("🔢 ForecastingService: x = $x")
        println("🔢 ForecastingService: y = $y")
        
        // Calculate linear regression slope
        val sumX = x.sum()
        val sumY = y.sum()
        val sumXY = x.zip(y).sumOf { it.first * it.second }
        val sumXX = x.sumOf { it * it }
        
        println("🔢 ForecastingService: sumX = $sumX")
        println("🔢 ForecastingService: sumY = $sumY")
        println("🔢 ForecastingService: sumXY = $sumXY")
        println("🔢 ForecastingService: sumXX = $sumXX")
        
        val denominator = n * sumXX - sumX * sumX
        println("🔢 ForecastingService: denominator = $denominator")
        
        val slope = if (denominator != 0.0) {
            val numerator = n * sumXY - sumX * sumY
            println("🔢 ForecastingService: numerator = $numerator")
            numerator / denominator
        } else {
            0.0
        }
        val intercept = (sumY - slope * sumX) / n
        
        println("🔢 ForecastingService: slope = $slope")
        println("🔢 ForecastingService: intercept = $intercept")
        
        // Generate future projections
        val projections = (1..months).map { monthOffset ->
            val futureX = n + monthOffset
            val projection = intercept + slope * futureX
            val result = maxOf(0.0, projection)
            println("🔢 ForecastingService: month $monthOffset: futureX=$futureX, projection=$projection, result=$result")
            result
        }
        
        println("🔢 ForecastingService: Final projections = $projections")
        return projections
    }
}

/**
 * Get week number in year (simplified calculation)
 */
private fun getWeekOfYear(date: kotlinx.datetime.LocalDate): Int {
    val jan1 = kotlinx.datetime.LocalDate(date.year, 1, 1)
    val daysDiff = date.toEpochDays() - jan1.toEpochDays()
    return (daysDiff / 7) + 1
}

/**
 * Enhanced function to process raw transaction data with category information
 * Now supports different time intervals (Weekly, Quarterly, Monthly)
 */
fun applyAdvancedAnalyticsWithCategories(
    transactions: List<models.Transaction>,
    timeIntervalMode: String = "Monthly"
): AdvancedAnalyticsData {
    println("⚡ applyAdvancedAnalyticsWithCategories: Starting with ${transactions.size} transactions, mode: $timeIntervalMode")

    if (transactions.isEmpty()) {
        println("🔴 applyAdvancedAnalyticsWithCategories: No transactions, returning empty data")
        return AdvancedAnalyticsData(
            originalData = emptyList(),
            trendDirection = TrendDirection("NO_DATA", 0.0, 0.0, 0.0),
            anomalies = emptyList(),
            forecast = emptyList(),
            recommendations = listOf("No data available for analysis."),
            categoryData = emptyMap(),
            availableCategories = emptyList(),
            selectedCategory = null
        )
    }

    // Show sample transactions for debugging
    println("⚡ Sample transactions (first 3):")
    transactions.take(3).forEach { tx ->
        println("  ${tx.name}: amount=${tx.amount}, date=${tx.date}, category=${tx.personalFinanceCategoryPrimary}")
    }

    // Process transactions by category and time period
    val expenseTransactions = transactions.filter { it.amount < 0 }
    println("⚡ Filtered to ${expenseTransactions.size} expense transactions (amount < 0)")

    val categoryPeriodData = mutableMapOf<String, MutableMap<String, Double>>()

    // Generate complete period range - only include historical periods (no future periods with $0)
    // Future periods should only be shown as forecast projections, not as actual data points
    val (backwardPeriods, forwardPeriods) = when (timeIntervalMode) {
        "Weekly" -> Pair(52, 0) // 52 weeks back, no future weeks
        "Quarterly" -> Pair(8, 0) // 8 quarters back, no future quarters
        else -> Pair(24, 0) // 24 months back (Monthly mode)
    }

    println("⚡ Generating periods: $backwardPeriods backward, $forwardPeriods forward")

    // Generate all period labels and their corresponding date ranges
    val periodRanges = mutableListOf<Triple<String, kotlinx.datetime.LocalDate, kotlinx.datetime.LocalDate>>()

    // Generate backward periods (historical data)
    for (periodOffset in -backwardPeriods..-1) {
        val periodLabel = utils.DateUtils.calculatePeriodLabel(timeIntervalMode, periodOffset)
        val (startDate, endDate) = utils.DateUtils.calculateDateRange(timeIntervalMode, periodOffset)
        periodRanges.add(Triple(periodLabel, startDate, endDate))
    }

    // Add current period (offset 0)
    val currentPeriodLabel = utils.DateUtils.calculatePeriodLabel(timeIntervalMode, 0)
    val (currentStart, currentEnd) = utils.DateUtils.calculateDateRange(timeIntervalMode, 0)
    periodRanges.add(Triple(currentPeriodLabel, currentStart, currentEnd))

    // Generate forward periods (forecast data for Weekly and Quarterly)
    for (periodOffset in 1..forwardPeriods) {
        val periodLabel = utils.DateUtils.calculatePeriodLabel(timeIntervalMode, periodOffset)
        val (startDate, endDate) = utils.DateUtils.calculateDateRange(timeIntervalMode, periodOffset)
        periodRanges.add(Triple(periodLabel, startDate, endDate))
    }

    // Get sorted period labels
    val sortedPeriods = periodRanges.map { it.first }
    println("⚡ Generated ${sortedPeriods.size} period labels. First 3: ${sortedPeriods.take(3)}")

    // Map each transaction to its period
    var mappedCount = 0
    var skippedCount = 0

    expenseTransactions.forEach { transaction ->
        val category = transaction.personalFinanceCategoryPrimary
            ?: transaction.category?.split(",")?.firstOrNull()?.trim()
            ?: "Other"

        val transactionDate = kotlinx.datetime.LocalDate.parse(transaction.date.substring(0, 10))

        // Find which period this transaction belongs to
        val periodInfo = periodRanges.find { (_, startDate, endDate) ->
            transactionDate >= startDate && transactionDate <= endDate
        }

        if (periodInfo == null) {
            // Transaction is outside our date range, skip it
            skippedCount++
            return@forEach
        }

        val periodKey = periodInfo.first
        val amount = kotlin.math.abs(transaction.amount)

        val categoryMap = categoryPeriodData.getOrPut(category) { mutableMapOf() }
        categoryMap[periodKey] = (categoryMap[periodKey] ?: 0.0) + amount
        mappedCount++
    }

    println("⚡ Mapped $mappedCount transactions to periods, skipped $skippedCount outside date range")
    println("⚡ Found ${categoryPeriodData.keys.size} categories: ${categoryPeriodData.keys.take(5)}")
    
    // Create per-category period data and aggregated data
    val categoryData = mutableMapOf<String, List<Pair<String, Double>>>()
    val aggregatedPeriodData = mutableMapOf<String, Double>()
    
    // Initialize aggregated data with zeros for all periods
    sortedPeriods.forEach { period ->
        aggregatedPeriodData[period] = 0.0
    }
    
    // Ensure we have at least some categories even if no transactions
    val allCategories = if (categoryPeriodData.isEmpty()) {
        setOf("Other") // Default category if no transactions
    } else {
        categoryPeriodData.keys
    }
    
    allCategories.forEach { category ->
        val periodAmounts = categoryPeriodData[category] ?: emptyMap()
        val categoryTimeSeries = sortedPeriods.map { period ->
            val amount = periodAmounts[period] ?: 0.0
            aggregatedPeriodData[period] = (aggregatedPeriodData[period] ?: 0.0) + amount
            period to amount
        }
        categoryData[category] = categoryTimeSeries
    }
    
    // Create overall aggregated time series
    val overallTimeSeries = sortedPeriods.map { period ->
        period to (aggregatedPeriodData[period] ?: 0.0)
    }
    
    println("🎯 applyAdvancedAnalyticsWithCategories: Created overallTimeSeries with ${overallTimeSeries.size} periods")
    println("🎯 applyAdvancedAnalyticsWithCategories: First 3 entries: ${overallTimeSeries.take(3)}")
    println("🎯 applyAdvancedAnalyticsWithCategories: Last 3 entries: ${overallTimeSeries.takeLast(3)}")
    
    // Debug: Show non-zero entries
    val nonZeroOverall = overallTimeSeries.filter { it.second > 0.0 }
    if (nonZeroOverall.isNotEmpty()) {
        println("🎯 applyAdvancedAnalyticsWithCategories: Non-zero entries: $nonZeroOverall")
    } else {
        println("🎯 applyAdvancedAnalyticsWithCategories: ERROR - All entries are zero!")
        println("🎯 applyAdvancedAnalyticsWithCategories: aggregatedPeriodData contents:")
        aggregatedPeriodData.forEach { (period, amount) ->
            println("🎯 applyAdvancedAnalyticsWithCategories:   '$period' -> $amount")
        }
    }
    
    // Get available categories sorted by total spending
    val availableCategories = categoryData.keys.sortedByDescending { category ->
        categoryData[category]?.sumOf { it.second } ?: 0.0
    }
    
    println("🎯 DEBUG: categoryPeriodData keys: ${categoryPeriodData.keys}")
    println("🎯 DEBUG: categoryData keys: ${categoryData.keys}")
    println("🎯 DEBUG: allCategories: $allCategories")
    println("🎯 DEBUG: availableCategories: $availableCategories")
    
    // Apply analytics to the overall data with category support
    val result = applyAdvancedAnalyticsToSpendingData(
        monthlySpending = overallTimeSeries,
        categoryData = categoryData,
        availableCategories = availableCategories,
        selectedCategory = null
    )
    
    println("🎯 DEBUG: Final result availableCategories: ${result.availableCategories}")
    
    return result
}

/**
 * Filter analytics data to show only a specific category
 */
fun filterAnalyticsByCategory(data: AdvancedAnalyticsData, category: String?): AdvancedAnalyticsData {
    if (category == null || !data.availableCategories.contains(category)) {
        // Return to showing all categories
        return data.copy(selectedCategory = null)
    }
    
    // Get category-specific data but preserve the original data structure
    val categoryTimeSeries = data.categoryData[category] ?: emptyList()
    
    // Instead of reprocessing, just update the originalData to show only the selected category
    // and preserve all the other analytics (forecast, recommendations, etc.)
    return data.copy(
        originalData = categoryTimeSeries,
        selectedCategory = category
    )
}